import {Tooltip} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import useAntibodyMetadata from 'molbio/antibody-engineering/useAntibodyMetadata';
import {
  getAntibodyVisibilitySettings,
  shouldShowAntibodyTools,
} from 'molbio/antibody-engineering/visibilityHelpers';
import {useStoredResidueNumbering} from 'molbio/maps/linear/StoredAntibodyNumbering';
import {STORED_ANTIBODY_NUMBERING, VisibilityOptions} from 'settings/visibility-options/VisibilityOptions';
import {ORANGE_DARK} from 'ui-library/Colors';

/**
 * Summarizes the antibody numbering on the map: the scheme, organism and chain of the stored numbering,
 * or the best-match organism and chains of the legacy tools.
 */
export default function AntibodyToolsMetadata({
  aaSequenceId,
  aaSequenceVersionId,
  aminoAcids,
  visibilityOptions,
}: {
  aaSequenceId: string;
  aaSequenceVersionId: number;
  aminoAcids: string;
  visibilityOptions: VisibilityOptions;
}): JSX.Element {
  const [numberingScheme, cdrDefinition] = getAntibodyVisibilitySettings(visibilityOptions);
  const [antibodyMessage, organismInfo] = useAntibodyMetadata(aminoAcids, numberingScheme, cdrDefinition);
  const storedNumbering = useStoredResidueNumbering({
    aaSequenceId,
    aaSequenceVersionId,
    residues: aminoAcids,
    skip: !visibilityOptions.getOption(STORED_ANTIBODY_NUMBERING.name)?.get('visible'),
  });

  if (storedNumbering != null) {
    return (
      <div className={css(styles.antibodyMetadataContainer)}>
        <Tooltip label="Antibody Numbering">
          <span className={css(styles.organismInfo)}>{storedNumbering.summary}</span>
        </Tooltip>
      </div>
    );
  }
  if (!shouldShowAntibodyTools(visibilityOptions)) {
    return <></>;
  }
  return (
    <div className={css(styles.antibodyMetadataContainer)}>
      {antibodyMessage && (
        <Tooltip label={antibodyMessage}>
          <span className={css(styles.antibodyMessage)}>{antibodyMessage}</span>
        </Tooltip>
      )}
      {organismInfo && (
        <Tooltip label="Antibody Chain(s) (Best Match)">
          <span className={css(styles.organismInfo)}>{organismInfo}</span>
        </Tooltip>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  antibodyMetadataContainer: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    paddingRight: 15,
    paddingBottom: 2,
  },
  antibodyMessage: {
    color: ORANGE_DARK,
  },
  organismInfo: {
    marginLeft: 5,
  },
});
